/**
 * Typed accessors over the baked subdivision dataset — THE IDENTITY HALF.
 *
 * Same split, same reason, as `index.ts` versus `facts.ts`: nothing here is an
 * answer to anything. A subdivision's name, outline, centroid and bounding box
 * are what the globe has to draw and what the reveal has to fly to, and a
 * player who reads all of it off the bundle has learned that Ohio is where Ohio
 * is — which they can see.
 *
 * The CAPITAL is not here. It is rung 2's answer key and it lives in
 * `statefacts.ts`, imported by the ladder builder and by nothing a ranked
 * client loads.
 *
 * The polygons are not here either, but for a different reason: they are 250 KB
 * and only one of three game modes needs them, so the client fetches them on
 * demand through this package's `./us-states.json` export. See the note at the
 * foot of this file.
 */

import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { Tier } from '@geogame/shared';

import geoJson from './generated/us-states-geo.json' with { type: 'json' };
import { CURATED_SUBDIVISIONS, SUBDIVISION_POOL_SIZE } from './statecurated';
import type { SubdivisionFeatureProperties, SubdivisionGeo } from './types';

export type { SubdivisionFeatureProperties, SubdivisionGeo } from './types';

export type SubdivisionFeature = Feature<Geometry, SubdivisionFeatureProperties>;
export type SubdivisionFeatureCollection = FeatureCollection<
  Geometry,
  SubdivisionFeatureProperties
>;

/**
 * One playable subdivision, as everything outside the ladder builder sees it.
 *
 * `clue` is curated and `everything else` is baked — see `statecurated.ts` for
 * what a clue is allowed to say, which is less than you would think.
 */
export interface SubdivisionRecord extends SubdivisionGeo {
  /** Smaller is more recognisable; tier pools are prefixes of this order. */
  readonly fameRank: number;
  /** A unique, stable clue naming neither the subdivision nor its capital. */
  readonly clue: string;
}

// Typed as `unknown` on purpose — see src/generated/generated.d.ts.
const GEO = geoJson as unknown as SubdivisionGeo[];

const GEO_INDEX: ReadonlyMap<string, SubdivisionGeo> = new Map(
  GEO.map((g) => [g.code, g] as const),
);

/**
 * The pool, in fame order. Built from the CURATED list rather than from the
 * baked one so that a subdivision with geometry but no clue — every
 * subdivision the bake draws and does not pool, starting with the District of
 * Columbia — is drawn on the map and never asked for.
 */
const SUBDIVISIONS: readonly SubdivisionRecord[] = CURATED_SUBDIVISIONS.map((curated, index) => {
  const geo = GEO_INDEX.get(curated.code);
  if (!geo) {
    throw new Error(
      `states: curated subdivision ${curated.code} has no baked geometry — run npm run bake:states`,
    );
  }
  return { ...geo, fameRank: index + 1, clue: curated.clue };
});

const INDEX: ReadonlyMap<string, SubdivisionRecord> = new Map(
  SUBDIVISIONS.map((s) => [s.code, s] as const),
);

/** Every playable subdivision, ordered by fameRank ascending. */
export function allSubdivisions(): readonly SubdivisionRecord[] {
  return SUBDIVISIONS;
}

/** Look up one subdivision by ISO 3166-2. Undefined for one that is only drawn. */
export function bySubdivisionCode(code: string): SubdivisionRecord | undefined {
  return INDEX.get(code.toUpperCase());
}

/** The scored pool for a tier: a prefix of the fame order (spec section 3). */
export function subdivisionPoolForTier(tier: Tier): readonly SubdivisionRecord[] {
  return SUBDIVISIONS.slice(0, SUBDIVISION_POOL_SIZE[tier]);
}

/*
 * THE POLYGONS ARE NOT EXPORTED FROM HERE, AND THAT IS DELIBERATE.
 *
 * `us-states.json` is a quarter of a megabyte and two of the three game modes
 * never draw a state line, so it has to be fetched rather than imported — and
 * WHO fetches it is a bundling decision, which belongs to the bundle. The
 * client loads it in `lib/regions.ts`, through this package's
 * `./us-states.json` export, and the types it lands in are the two above.
 *
 * It was briefly an async accessor here. A dynamic `import(..., { with: { type:
 * 'json' } })` is correct for Node and wrong for a dev server, which serves
 * JSON modules as JavaScript and fails the assertion — so the accessor worked
 * everywhere except the browser, which is the only caller.
 */
